import React from 'react';
import { Avatar, Box, LinearProgress, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { SX, CARD_ACCENTS, BADGES_BY_KEY, initialsOf, levelProgress } from './leaderboardData';

const CARD_W = 320;

// Hoisted so the object identity is stable across renders — emotion re-hashes
// an sx object every time it sees a new one, and a rail of these cards renders
// the same shell over and over.
const SHELL_SX = {
  position: 'relative',
  overflow: 'hidden',
  minWidth: CARD_W,
  maxWidth: CARD_W,
  flex: `0 0 ${CARD_W}px`,
  p: 2.25,
  pt: 2.5,
  borderRadius: `${SX.radius.lg}px`,
  background: SX.card,
  border: `1px solid ${SX.borderStrong}`,
  boxShadow: SX.shadowSoft,
};

const XP_BAND_SX = {
  mt: 2,
  px: 1.5,
  py: 1.1,
  borderRadius: `${SX.radius.sm}px`,
  background: SX.cardSoft,
  border: `1px solid ${SX.border}`,
};

const EMPTY_SX = {
  mt: 2.25,
  py: 3.25,
  borderRadius: `${SX.radius.sm}px`,
  border: `1px dashed ${SX.border}`,
  background: SX.cardSoft,
};

/**
 * One batch's top performer, as a white card: a hairline accent on top, a
 * bordered surface that lifts on hover, and icons carrying the meaning
 * (batch, crown, XP, level, badges) rather than a colour wash.
 *
 * `student` is a leaderboard row, or null while the batch is still loading or
 * has earned no XP yet. `index` picks the accent from the cycling palette so a
 * row of batches reads as one set.
 *
 * Memoised: the rail re-renders whenever its window grows, and a card's props
 * are plain values, so already-mounted cards can skip the work entirely.
 */
function TopPerformerCard({ batchName, student, loading, index = 0 }) {
  const accent = CARD_ACCENTS[index % CARD_ACCENTS.length];

  if (loading) {
    return (
      <Box sx={SHELL_SX}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Skeleton variant="rounded" width={34} height={34} />
          <Skeleton variant="text" width="55%" />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
          <Skeleton variant="circular" width={62} height={62} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="45%" />
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={38} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const progress = student ? levelProgress(student.xp) : null;

  return (
    <Box
      sx={{
        ...SHELL_SX,
        transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: SX.shadowHover,
          borderColor: `${accent}55`,
        },
      }}
    >
      {/* accent hairline — the only colour the card commits to */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: accent }} />

      {/* header: batch identity + TOP pill */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: `${SX.radius.sm}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${accent}14`,
              color: accent,
            }}
          >
            <Icon icon="solar:users-group-rounded-bold-duotone" width={19} height={19} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: SX.textMuted }}>
              BATCH
            </Typography>
            <Tooltip title={batchName || ''} arrow>
              <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 800, color: SX.text, maxWidth: 165 }}>
                {batchName}
              </Typography>
            </Tooltip>
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.4}
          sx={{
            flexShrink: 0,
            px: 0.9,
            py: 0.35,
            borderRadius: 999,
            background: 'rgba(212,160,23,0.12)',
            border: '1px solid rgba(212,160,23,0.30)',
            color: '#A97A0B',
          }}
        >
          <Icon icon="solar:cup-star-bold-duotone" width={13} height={13} />
          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4 }}>TOP</Typography>
        </Stack>
      </Stack>

      {student ? (
        <>
          {/* identity */}
          <Stack direction="row" alignItems="center" spacing={1.75} sx={{ mt: 2.25 }}>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={student.image || undefined}
                alt={student.name}
                sx={{
                  width: 62,
                  height: 62,
                  fontSize: 21,
                  fontWeight: 800,
                  color: accent,
                  background: `${accent}14`,
                  border: `2px solid ${accent}40`,
                }}
              >
                {initialsOf(student.name)}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: '#fff',
                  border: '1px solid rgba(212,160,23,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="solar:crown-bold" width={14} height={14} color="#D4A017" />
              </Box>
            </Box>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography noWrap sx={{ fontSize: 16, fontWeight: 800, color: SX.text, lineHeight: 1.25 }}>
                {student.name}
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  mt: 0.75,
                  width: 'fit-content',
                  maxWidth: '100%',
                  px: 0.9,
                  py: 0.3,
                  borderRadius: 999,
                  background: `${student.level.color}14`,
                  border: `1px solid ${student.level.color}33`,
                  color: student.level.color,
                }}
              >
                <Icon icon={student.level.icon} width={14} height={14} />
                <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 800 }}>
                  {student.level.name}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* XP + badge count */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={XP_BAND_SX}
          >
            <Stack direction="row" alignItems="baseline" spacing={0.6}>
              <Icon icon="solar:bolt-bold-duotone" width={18} height={18} color={accent} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: SX.text, lineHeight: 1 }}>
                {student.xp.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: SX.textMuted }}>XP</Typography>
            </Stack>

            {student.badges.length > 0 ? (
              <Stack direction="row" alignItems="center" spacing={0.4}>
                {student.badges.slice(0, 3).map((key) => {
                  const badge = BADGES_BY_KEY[key];
                  if (!badge) return null;
                  return (
                    <Tooltip key={key} arrow title={`${badge.name} — ${badge.description}`}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fff',
                          border: `1px solid ${badge.color}33`,
                          color: badge.color,
                        }}
                      >
                        <Icon icon={badge.icon} width={14} height={14} />
                      </Box>
                    </Tooltip>
                  );
                })}
                {student.badges.length > 3 && (
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: SX.textMuted }}>
                    +{student.badges.length - 3}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>No badges yet</Typography>
            )}
          </Stack>

          {/* progress to the next tier */}
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress.pct}
              sx={{
                height: 6,
                borderRadius: 999,
                background: 'rgba(27,36,56,0.07)',
                '& .MuiLinearProgress-bar': { borderRadius: 999, background: student.level.color },
              }}
            />
            <Typography sx={{ mt: 0.7, fontSize: 11, color: SX.textMuted }}>
              {progress.next
                ? `${progress.toNext.toLocaleString()} XP to ${progress.next.name}`
                : 'Top of the ladder'}
            </Typography>
          </Box>
        </>
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={0.75}
          sx={EMPTY_SX}
        >
          <Icon icon="solar:ranking-linear" width={26} height={26} color={SX.textMuted} />
          <Typography sx={{ fontSize: 12.5, color: SX.textMuted, textAlign: 'center' }}>
            No XP earned in this batch yet.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default React.memo(TopPerformerCard);
